import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip } from "@fluentui/react-components";
import { Hamburger } from "@fluentui/react-nav-preview";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ModalDialog from "../components/ModalDialog";
import Breadcrumbs from "./breadcrumb/Breadcrumbs";
import Navbar from "./navbar/Navbar";
import Timer from "../pages/client/TimerFunction/TimerFunction";
import { AppDispatch, RootState } from "../redux/store";
import { doLogout } from "../redux/authSlice";
import { setActionListSelectedItem } from "../redux/appSettingsSlice";

import "./style.css";
import { getTimeAllocationList } from "../redux/timeAllocationSlice";
import { getContactTimeAllocationsListItems } from "../redux/Client/contacts";

type ComponentProps = Record<string, any>;

function ASCLayout(props: ComponentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();

  // Idle timer settings
  const timeout = 1800000; // 30 minutes
  const promptBeforeIdle = 10000; // 10 seconds
  const activeClient: any = useSelector(
    (state: RootState) => state.activeClient
  );
  // States
  const [remaining, setRemaining] = useState<number>(timeout / 1000);
  const [state, setState] = useState<"Active" | "Idle" | "Prompted">("Active");
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSidebarOpen, setSidebarOpen] = useState<boolean>(true);

  const isUtilities = location.pathname.startsWith("/utilities");
  const isAdminRoute = location.pathname.startsWith("/system-manager");
  const isCommission = location.pathname.startsWith("/commission");

  // Idle timer handlers
  const onIdle = () => {
    setState("Idle");
    setModalOpen(false);
    dispatch(doLogout());
  };

  const onActive = () => {
    setState("Active");
    setModalOpen(false);
  };

  const onPrompt = () => {
    setState("Prompted");
    setModalOpen(true);
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime]);
  useEffect(() => {
    if (!["action-list", "/action-list"].includes(location.pathname)) {
      dispatch(setActionListSelectedItem({}));
    }
  }, [location.pathname, dispatch]);
  const handleStillHere = () => {
    activate();
  };

  const renderHamburgerWithTooltip = () => (
    <Tooltip content="Navigation" relationship="label">
      <Hamburger onClick={() => setSidebarOpen(!isSidebarOpen)} />
    </Tooltip>
  );

  const excludedRoutes = [
    "clients",
    "system-manager",
    "utilities",
    "commission",
    "assyst_portal",
  ];
  const isExcludedRoute =
    location?.pathname &&
    excludedRoutes.some((route) => location.pathname.includes(route));
  const isFloatingMenuOpen = useSelector(
    (state: any) => state.opentimer.storedValue
  );

  const getTimerData = (id: any) => {
    dispatch(getTimeAllocationList(id));
    dispatch(getContactTimeAllocationsListItems(id));
  };

  return (
    <>
      {/* Session Timeout Modal */}
      <ModalDialog
        options={{
          open: isModalOpen,
          title: "Your session is about to expire",
          content_line_2:
            "You're being timed out due to inactivity. Please choose to stay logged in or log off.",
          content_line_3: "Otherwise, you will be logged off automatically.",
          yes: {
            label: `Stay Logged In (${remaining}s)`,
            type: "primary",
            onclick: handleStillHere,
          },
          no: {
            label: "Log Off",
            onclick: () => dispatch(doLogout()),
          },
        }}
      />

      {/* Page Layout */}
      <div className="body-wrapper">
        <div className="page-wrapper">
          {/* Header */}
          <div className="top-header-bar">
            <Header />
          </div>

          <div
            className={isAdminRoute || isUtilities || isCommission ? "" : ""}
          >
            {/* Main Content */}
            <div className={isSidebarOpen ? "content" : ""}>
              {!isSidebarOpen && renderHamburgerWithTooltip()}
              <div
                className={
                  isAdminRoute || isUtilities || isCommission
                    ? ""
                    : "content-area"
                }
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Navbar />
                  {isFloatingMenuOpen === true ? (
                    <Timer getTimerData={getTimerData} />
                  ) : (
                    ""
                  )}
                </div>
                <div className="content-area">
                  <div className="breadcrumbs-and-button">
                    {isExcludedRoute ? <></> : <Breadcrumbs />}
                  </div>

                  <Outlet />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <Footer />
        </div>
      </div>
    </>
  );
}

export default ASCLayout;
